import React, { useMemo } from 'react';
import {
  Card,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  Alert,
  Divider,
  Checkbox,
  Code,
  Loader,
  Tooltip
} from '@mantine/core';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconEdit,
  IconRefresh,
  IconShieldCheck
} from '@tabler/icons-react';
import PreconditionsBadge from './PreconditionsBadge';

/**
 * Shared result list shown by every importer (DiagramImporter, ExcelImporter,
 * and any future ICM/UCCX/CCE importer) after `/imports` returns. One visual
 * for "Bedrock produced N plans — review, edit, save, or send to a batch run."
 *
 * Props:
 *   plans         — array of v1 TestPlan objects returned by `/imports`
 *   modelId       — Bedrock model id (badge)
 *   retries       — schema-retry count (badge; green at 0, yellow otherwise)
 *   warnings      — string[] from the importer (shown in a yellow alert)
 *   sourceLabel   — text shown after "Verify & edit" badge, e.g. "Excel import"
 *   onVerify(plans) — callback when the user clicks the primary CTA
 *
 *   Optional selection mode (kept opt-in so non-selecting importers stay simple):
 *     selectable    — when true, render checkboxes on each card
 *     selectedIndexes — Set<number> of currently-selected plan indices
 *     onToggleSelect(i) — flip selection on one plan
 *     extraHeaderActions — slot for "Select all / Clear / Run N selected"
 *                          buttons (DiagramImporter passes these in)
 *
 *   Optional quality-judge UX (kept opt-in so non-judging callers stay simple):
 *     evaluating     — boolean; render "Evaluating quality…" loader badge
 *     qualityScore   — 0..1 from `/imports/judge`; drives badge color
 *     qualityFindings — array of {itemId, score, severity, reason, suggestion}
 *                       from the same judge call
 *     onRegenerate(findings) — callback when user clicks "Regenerate addressing
 *                       N findings". Findings are pre-filtered to non-info.
 */
function SynthesizedPlanList({
  plans,
  modelId,
  retries = 0,
  warnings = [],
  sourceLabel,
  onVerify,
  selectable = false,
  selectedIndexes,
  onToggleSelect,
  extraHeaderActions = null,
  evaluating = false,
  qualityScore,
  qualityFindings = [],
  onRegenerate
}) {
  // Bucket findings by plan id so we can drop them under the matching card.
  // perItem.itemId is either "<planId>" or "<planId>:<stepId>".
  const findingsByPlan = useMemo(() => {
    const m = new Map();
    (qualityFindings || []).forEach((f) => {
      if (!f || typeof f.itemId !== 'string') return;
      const planId = f.itemId.split(':')[0];
      if (!planId || planId === '_input') return;
      if (!m.has(planId)) m.set(planId, []);
      m.get(planId).push(f);
    });
    return m;
  }, [qualityFindings]);

  // Findings whose itemId doesn't match any plan (e.g., set-level "_input"
  // errors or plan ids the model fabricated) belong in a top-level callout.
  const orphanFindings = useMemo(() => {
    if (!Array.isArray(qualityFindings)) return [];
    const planIds = new Set((plans || []).map((p) => p?.metadata?.id).filter(Boolean));
    return qualityFindings.filter((f) => {
      if (!f || typeof f.itemId !== 'string') return false;
      const planId = f.itemId.split(':')[0];
      return !planIds.has(planId);
    });
  }, [qualityFindings, plans]);

  // Severity is the right input for the regenerate filter (kept in
  // findingsForRegenerate in importsClient.js — duplicated cheaply here
  // so the badge count matches what we'd actually feed back).
  const regenerateFindings = useMemo(
    () => (qualityFindings || []).filter((f) => f && f.severity !== 'info'),
    [qualityFindings]
  );

  if (!plans || plans.length === 0) return null;

  return (
    <Card withBorder shadow="sm" radius="md" p="lg">
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Group gap="xs">
          <Title order={4}>Generated plans</Title>
          <Badge variant="light" color="violet">{plans.length} plans</Badge>
          {modelId && <Badge variant="outline" color="gray">model: {modelId}</Badge>}
          <Badge variant="outline" color={retries === 0 ? 'green' : 'yellow'}>
            retries: {retries}
          </Badge>
          {sourceLabel && (
            <Badge variant="dot" color="violet">{sourceLabel}</Badge>
          )}
          <QualityBadge evaluating={evaluating} score={qualityScore} />
        </Group>
        <Group gap="xs">
          {extraHeaderActions}
          {onRegenerate && regenerateFindings.length > 0 && (
            <Tooltip label="Re-run the importer with the judge's findings appended to the prompt.">
              <Button
                variant="light"
                color="yellow"
                leftSection={<IconRefresh size={14} />}
                onClick={() => onRegenerate(regenerateFindings)}
              >
                Regenerate addressing {regenerateFindings.length} finding{regenerateFindings.length === 1 ? '' : 's'}
              </Button>
            </Tooltip>
          )}
          <Button
            color="violet"
            leftSection={<IconEdit size={14} />}
            onClick={() => onVerify?.(plans)}
          >
            Verify &amp; edit plans
          </Button>
        </Group>
      </Group>

      {warnings.length > 0 && (
        <Alert color="yellow" icon={<IconAlertCircle size={16} />} mb="sm">
          {warnings.join(' ')}
        </Alert>
      )}

      {orphanFindings.length > 0 && (
        <Alert
          color="orange"
          icon={<IconAlertTriangle size={16} />}
          title="Set-level quality findings"
          mb="sm"
        >
          <Stack gap={4}>
            {orphanFindings.map((f, i) => (
              <Text key={i} size="xs">
                <strong>{f.itemId}:</strong> {f.reason}
                {f.suggestion ? ` — ${f.suggestion}` : ''}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      <Divider mb="sm" />

      <Stack gap="xs">
        {plans.map((plan, i) => {
          const checked = selectable ? selectedIndexes?.has?.(i) : false;
          const stepCount = plan.steps?.length ?? 0;
          const name = plan.metadata?.name || plan.metadata?.id || `Plan ${i + 1}`;
          const id = plan.metadata?.id;
          const destination = plan.target?.destination;
          const tags = plan.metadata?.tags || [];
          const planFindings = id ? findingsByPlan.get(id) || [] : [];
          const hasFindings = planFindings.length > 0;
          const worstSeverity = hasFindings
            ? planFindings.reduce(
                (acc, f) =>
                  f.severity === 'error'
                    ? 'error'
                    : f.severity === 'warn' && acc !== 'error'
                    ? 'warn'
                    : acc,
                'info'
              )
            : null;
          return (
            <Card key={i} withBorder padding="sm" radius="sm">
              <Group justify="space-between" wrap="nowrap" gap="sm">
                <Group gap="sm" wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                  {selectable && (
                    <Checkbox
                      checked={checked}
                      onChange={() => onToggleSelect?.(i)}
                      mt={4}
                    />
                  )}
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={600} lineClamp={1}>{name}</Text>
                    <Group gap="xs" wrap="wrap">
                      <Badge size="xs" variant="light" color="violet">
                        {stepCount} step{stepCount === 1 ? '' : 's'}
                      </Badge>
                      {destination && (
                        <Badge size="xs" variant="light" color="gray">{destination}</Badge>
                      )}
                      {id && (
                        <Text size="xs" c="dimmed">id: <Code>{id}</Code></Text>
                      )}
                      {tags.slice(0, 3).map((t) => (
                        <Badge key={t} size="xs" variant="dot" color="violet">{t}</Badge>
                      ))}
                      {tags.length > 3 && (
                        <Text size="xs" c="dimmed">+{tags.length - 3} more</Text>
                      )}
                      <PreconditionsBadge preconditions={plan?.metadata?.preconditions} />

                      {hasFindings && (
                        <Badge
                          size="xs"
                          variant="filled"
                          color={
                            worstSeverity === 'error'
                              ? 'red'
                              : worstSeverity === 'warn'
                              ? 'yellow'
                              : 'gray'
                          }
                        >
                          {planFindings.length} finding{planFindings.length === 1 ? '' : 's'}
                        </Badge>
                      )}
                    </Group>
                  </Stack>
                </Group>
              </Group>

              {hasFindings && (
                <Alert
                  mt="xs"
                  color={
                    worstSeverity === 'error'
                      ? 'red'
                      : worstSeverity === 'warn'
                      ? 'yellow'
                      : 'blue'
                  }
                  icon={<IconAlertCircle size={14} />}
                  variant="light"
                >
                  <Stack gap={4}>
                    {planFindings.map((f, fi) => (
                      <Text key={fi} size="xs">
                        <strong>
                          [{f.severity}] {f.itemId.includes(':') ? f.itemId.split(':')[1] : f.itemId}:
                        </strong>{' '}
                        {f.reason}
                        {f.suggestion ? ` — ${f.suggestion}` : ''}
                      </Text>
                    ))}
                  </Stack>
                </Alert>
              )}
            </Card>
          );
        })}
      </Stack>
    </Card>
  );
}

function QualityBadge({ evaluating, score }) {
  if (evaluating) {
    return (
      <Badge variant="outline" color="gray" leftSection={<Loader size={10} />}>
        evaluating quality…
      </Badge>
    );
  }
  if (typeof score !== 'number' || Number.isNaN(score)) return null;
  const pct = Math.round(score * 100);
  const color = score >= 0.8 ? 'green' : score >= 0.6 ? 'yellow' : 'red';
  return (
    <Tooltip label="LLM-as-a-judge advisory grade. Drops automatically when the importer surfaces real problems.">
      <Badge variant="filled" color={color} leftSection={<IconShieldCheck size={12} />}>
        quality: {pct}%
      </Badge>
    </Tooltip>
  );
}

export default SynthesizedPlanList;
